import React, { useState, ChangeEvent } from 'react';
import Modal from 'components/Modal/Modal';
import Button from 'components/Button';
import Input from 'components/Input';
import Auth from '@aws-amplify/auth';
import QRCode from 'qrcode.react';
import { ReactComponent as MFAIcon } from 'assets/heroicons/icon-mobile.svg';
import { useAuthWrapperContext } from 'toolbox/src/components/AuthWrapper/AuthWrapper';

const SetupMFAButton = ({ callbackFn }) => {
  const { user, username, refreshAuthState } = useAuthWrapperContext();

  const [preferredMFA, setPreferredMFA] = useState('NOMFA');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [testCode, setTestCode] = useState('');

  const getPreferredMFA = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const pref = await Auth.getPreferredMFA(user, {
        bypassCache: false,
      });
      setPreferredMFA(pref);
      if (pref === 'NOMFA') getQRCode();
    } catch (err) {
      console.warn(err);
    }
  };

  const getQRCode = async () => {
    try {
      const secretCode = await Auth.setupTOTP(user);
      const url = `otpauth://totp/JCAP:${username}?secret=${secretCode}&issuer=${window.location.hostname}`;
      setQrCode(url);
      setSecret(secretCode);
    } catch (err) {
      console.warn(err);
    }
  };

  const enableMFA = async (callbackFunction) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.verifyTotpToken(user, testCode)
        .then(() => Auth.setPreferredMFA(user, 'TOTP'))
        .catch((err) => console.warn(err));
      await getPreferredMFA();
      refreshAuthState();
      callbackFn && callbackFn();
      alert('MFA has been enabled');
      callbackFunction(); // hide Modal
    } catch (verificationError) {
      console.warn(verificationError);
    }
  };

  return (
    <Modal
      onOpen={() => {
        setTestCode('');
        getPreferredMFA();
      }}
      button={(show) => (
        <Button
          onClick={() => {
            show();
          }}
          className="mr-2 btn btn-white"
        >
          <MFAIcon className="inline w-5 h-5 mb-1 -ml-1 text-gray-500" /> Setup
          MFA
        </Button>
      )}
      title="Setup MFA"
      content={() => (
        <div className="flex items-start overflow-hidden">
          {(!preferredMFA || preferredMFA === 'NOMFA') && (
            <div className="w-48 p-2 mr-5 bg-white border-4 border-gray-500 rounded">
              <QRCode value={qrCode} renderAs="svg" />
              <a
                href={qrCode}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-3 font-mono text-xs leading-tight tracking-tight text-gray-700 break-all hover:underline"
              >
                {qrCode}
              </a>
            </div>
          )}
          <div>
            {!preferredMFA || preferredMFA === 'NOMFA' ? (
              <>
                <p className="mb-4 font-semibold break-words">
                  Your account is not yet protected with Multi-Factor
                  Authentication (MFA)
                </p>
                <p className="mb-4 break-words">
                  Use the <b>QR Code</b> or <b>OTP link</b> (left) with your
                  preferred Authenticator app.
                </p>
                <p className="mb-2 break-words">
                  Examples (available in the appstore) include:
                </p>
                <ul className="mb-4 ml-5 list-disc">
                  <li>Microsoft Authenticator</li>
                  <li>Google Authenticator</li>
                  <li>Authy</li>
                  <li>Bitwarden Password Manager</li>
                </ul>
                <p className="mb-4 break-words">
                  If your app asks for a TOTP secret, enter this code:
                </p>
                <p className="mb-4 font-mono text-sm text-blue-700 break-all">
                  {secret}
                </p>
                <p className="mb-4 break-words">
                  Before MFA can be enabled you must enter a valid Test Code
                  from your Authenticator app:
                </p>
                <Input
                  autoFocus
                  label="Test Code"
                  autoComplete="one-time-code"
                  value={testCode}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setTestCode(e.currentTarget.value.replace(/[^\d+]/, ''))
                  }
                />
              </>
            ) : (
              <>
                <p className="mb-4 break-words">
                  Your account is configured to require Multi-Factor
                  Authentication (MFA)
                </p>
                <p className="mb-4 break-words">
                  If you need to setup a new Authenticator app, please disable
                  and then re-enable MFA.
                </p>
              </>
            )}
          </div>
        </div>
      )}
      footer={(hide) => (
        <>
          <Button
            className="mr-2 btn btn-white"
            onClick={() => {
              hide();
            }}
          >
            Cancel
          </Button>

          <Button
            className="btn btn-success"
            disabled={!testCode}
            onClick={() => {
              enableMFA(hide);
            }}
          >
            Enable MFA
          </Button>
        </>
      )}
    />
  );
};

export default SetupMFAButton;
